import { Badge, Card, CardContent, cn, Skeleton } from '@up2stay/up2promo-ui';
import { FileStack, Loader2 } from 'lucide-react';

import { useProfileTemplate } from '@/core/hooks/use-profile-template';
import type { ProfileTemplateSyncStatus } from '@/core/lib/profile-template-sync-status';

interface ProfileTemplateBannerProps {
  templateId: string | null | undefined;
  syncStatus: ProfileTemplateSyncStatus;
  forcePending?: boolean;
  dict: {
    label: string;
    linkedDescription: string;
    syncPending: string;
    statusActive: string;
    statusSyncing: string;
  };
}

export function ProfileTemplateBanner({
  templateId,
  syncStatus,
  forcePending = false,
  dict,
}: ProfileTemplateBannerProps) {
  const { template, isLoading } = useProfileTemplate(templateId);

  if (!templateId) return null;

  const isPending = syncStatus === 'pending' || forcePending;

  return (
    <Card
      className={cn(
        'overflow-hidden shadow-none',
        isPending ? 'border-amber-500/25' : 'border-border',
      )}
    >
      <CardContent className="flex items-center gap-3 p-4 sm:p-5">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
            isPending
              ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
              : 'bg-primary/10 text-primary',
          )}
        >
          {isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          ) : (
            <FileStack className="h-5 w-5" aria-hidden />
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {dict.label}
            </span>
            <Badge
              variant={isPending ? 'warning' : 'success'}
              className="font-normal"
            >
              {isPending ? dict.statusSyncing : dict.statusActive}
            </Badge>
          </div>

          {isLoading ? (
            <Skeleton className="h-5 w-40 max-w-full" />
          ) : template?.name ? (
            <p className="truncate text-base font-semibold leading-tight text-foreground">
              {template.name}
            </p>
          ) : null}

          <p className="text-sm leading-snug text-muted-foreground">
            {isPending ? dict.syncPending : dict.linkedDescription}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
